/**
 * @tanstack/react-query hooks for the Payroll CSV Bridge module
 * (G5.5 frontend). Thin wrappers over lib/api/payroll.ts so pages stay
 * declarative and caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-offboarding.ts / use-leave.ts:
 *   ["payroll", <area>, ...params]
 *
 * Mutations invalidate the run list AND the specific run the mutation
 * touched (the detail page reads the bundled snapshot — a per-employee
 * override patch changes both lists' net totals, and the lock changes
 * the locked/locked_at columns the list renders).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { payrollApi } from "@/lib/api";
import {
  PAYROLL_SETUP_QUERY_KEY,
  invalidatePayrollSetup,
} from "@/lib/payroll-setup-queries";
import {
  MY_SALARY_QUERY_KEY,
  employeeSalaryQueryKey,
  invalidateSalaryBreakup,
} from "@/lib/salary-queries";
import type {
  CreateCompRevisionLetterRequest,
  CreateSalaryRevisionRequest,
  PayrollSettings,
  PutPreviousEmploymentRequest,
  PutOpeningYTDRequest,
  CreatePayrollRunRequest,
  UpdateEmployeePayrollSettingsRequest,
  TenantSettings,
  UpdateTenantSettingsRequest,
  UpsertOverrideRequest,
  StatutoryBonusResponse,
  UpdateStatutoryBonusRowRequest,
} from "@/types/payroll";

export const payrollKeys = {
  all: ["payroll"] as const,
  runs: (year?: number) =>
    [...payrollKeys.all, "runs", year ?? "any"] as const,
  run: (id: number) => [...payrollKeys.all, "run", id] as const,
  myPayslips: () => [...payrollKeys.all, "me", "payslips"] as const,
  mySalary: () => MY_SALARY_QUERY_KEY,
  employeeSalary: (id: number) => employeeSalaryQueryKey(id),
  periodWarnings: () => [...payrollKeys.all, "period-warnings"] as const,
  employeeSettings: (id: number) =>
    [...payrollKeys.all, "employee-settings", id] as const,
  tenantSettings: () => ["tenant", "settings"] as const,
  salaryRevisions: (id: number) =>
    [...payrollKeys.all, "salary-revisions", id] as const,
  salaryArrears: () => [...payrollKeys.all, "salary-arrears"] as const,
  pfCeiling: () => [...payrollKeys.all, "pf-ceiling"] as const,
  statutoryBonus: (fy?: number) => [...payrollKeys.all, "statutory-bonus", fy ?? "default"] as const,
  previousEmployment: (id: number, fy?: number) =>
    [...payrollKeys.all, "previous-employment", id, fy ?? "current"] as const,
  openingYTD: (id: number, fy?: number) =>
    [...payrollKeys.all, "opening-ytd", id, fy ?? "current"] as const,
  payrollSettings: () => [...payrollKeys.all, "settings"] as const,
  setup: () => PAYROLL_SETUP_QUERY_KEY,
  compLetters: (employeeId?: number) =>
    [...payrollKeys.all, "comp-letters", employeeId ?? "mine"] as const,
};

// ---------------------------------------------------------------------------
// Wave 2 (migrations 113/114) — salary history, arrears, previous employer,
// payroll settings
// ---------------------------------------------------------------------------

export function useSalaryRevisions(employeeId: number, enabled = true) {
  return useQuery({
    queryKey: payrollKeys.salaryRevisions(employeeId),
    queryFn: ({ signal }) => payrollApi.listSalaryRevisions(employeeId, signal),
    enabled: enabled && Number.isFinite(employeeId) && employeeId > 0,
  });
}

/** Records a revision; refreshes the employee's history, the arrears list
 *  and every payroll query (open drafts may now be stale). */
export function useCreateSalaryRevision(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSalaryRevisionRequest) =>
      payrollApi.createSalaryRevision(employeeId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: payrollKeys.all });
      invalidatePayrollSetup(qc);
      void qc.invalidateQueries({ queryKey: ["hr"] });
    },
  });
}

export function useSalaryArrears(enabled = true) {
  return useQuery({
    queryKey: payrollKeys.salaryArrears(),
    queryFn: ({ signal }) => payrollApi.listSalaryArrears(signal),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** "PF too low" after the ₹25,000 ceiling (GET /payroll/pf-ceiling/affected).
 *  Under payrollKeys.all, so creating / locking a correction refreshes it. */
export function usePFCeilingAffected(enabled = true) {
  return useQuery({
    queryKey: payrollKeys.pfCeiling(),
    queryFn: ({ signal }) => payrollApi.getPFCeilingAffected(signal),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Recover PF a correction left owed in the open later regular draft (manual fallback). */
export function useRecoverPFOwed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { correctionRunId: number; employeeId: number }) =>
      payrollApi.recoverPFOwed({ correction_run_id: args.correctionRunId, employee_id: args.employeeId }),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Waive PF a correction left owed (the company bears it). */
export function useWaivePFOwed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { correctionRunId: number; employeeId: number; note: string }) =>
      payrollApi.waivePFOwed({ correction_run_id: args.correctionRunId, employee_id: args.employeeId, note: args.note }),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Statutory bonus for an FY (default: the last one ended). Under
 *  payrollKeys.all, so discarding / recalculating a draft refreshes it. */
export function useStatutoryBonus(fy?: number, enabled = true) {
  return useQuery({
    queryKey: payrollKeys.statutoryBonus(fy),
    queryFn: ({ signal }) => payrollApi.getStatutoryBonus(fy, signal),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Calculate / edit a row: the response is the whole page; store it. */
function useStatutoryBonusWrite<A>(fn: (args: A) => Promise<StatutoryBonusResponse>, fy?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.setQueryData(payrollKeys.statutoryBonus(fy), data);
    },
  });
}

export function useCalculateStatutoryBonus(fy?: number) {
  return useStatutoryBonusWrite(payrollApi.calculateStatutoryBonus, fy);
}

export function useUpdateStatutoryBonusRow(fy?: number) {
  return useStatutoryBonusWrite(
    (args: { id: number; employeeId: number; body: UpdateStatutoryBonusRowRequest }) =>
      payrollApi.updateStatutoryBonusRow(args.id, args.employeeId, args.body),
    fy,
  );
}

/** Adds the bonus to the open draft: every payroll query (the draft's
 *  rows) is refreshed. */
export function useApplyStatutoryBonus(fy?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollApi.applyStatutoryBonus(id),
    onSuccess: (data) => {
      qc.setQueryData(payrollKeys.statutoryBonus(fy), data);
      void qc.invalidateQueries({ queryKey: payrollKeys.all });
    },
  });
}

export function usePreviousEmployment(employeeId: number, fy?: number) {
  return useQuery({
    queryKey: payrollKeys.previousEmployment(employeeId, fy),
    queryFn: ({ signal }) =>
      payrollApi.getPreviousEmployment(employeeId, fy, signal),
    enabled: Number.isFinite(employeeId) && employeeId > 0,
  });
}

export function useSavePreviousEmployment(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { fy: number; body: PutPreviousEmploymentRequest }) =>
      payrollApi.putPreviousEmployment(employeeId, args.fy, args.body),
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: [...payrollKeys.all, "previous-employment", employeeId],
      }),
  });
}

export function useDeletePreviousEmployment(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fy: number) =>
      payrollApi.deletePreviousEmployment(employeeId, fy),
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: [...payrollKeys.all, "previous-employment", employeeId],
      }),
  });
}

// Salary paid by this employer before payroll moved to WorkRight
// (migration 128). A change moves open drafts' TDS: refresh every payroll
// query so a stale draft shows as such.
export function useOpeningYTD(employeeId: number, fy?: number) {
  return useQuery({
    queryKey: payrollKeys.openingYTD(employeeId, fy),
    queryFn: ({ signal }) => payrollApi.getOpeningYTD(employeeId, fy, signal),
    enabled: Number.isFinite(employeeId) && employeeId > 0,
  });
}

export function useSaveOpeningYTD(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { fy: number; body: PutOpeningYTDRequest }) =>
      payrollApi.putOpeningYTD(employeeId, args.fy, args.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: payrollKeys.all }),
  });
}

export function useDeleteOpeningYTD(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fy: number) => payrollApi.deleteOpeningYTD(employeeId, fy),
    onSuccess: () => void qc.invalidateQueries({ queryKey: payrollKeys.all }),
  });
}

export function usePayrollSettings(enabled = true) {
  return useQuery({
    queryKey: payrollKeys.payrollSettings(),
    queryFn: ({ signal }) => payrollApi.getPayrollSettings(signal),
    enabled,
  });
}

export function useUpdatePayrollSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PayrollSettings) =>
      payrollApi.updatePayrollSettings(body),
    onSuccess: (data) => {
      qc.setQueryData(payrollKeys.payrollSettings(), data);
      // Open drafts may now be stale (their TDS moves).
      void qc.invalidateQueries({ queryKey: payrollKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — correction runs, period warnings, per-employee payroll settings
// ---------------------------------------------------------------------------

export function useCreateCorrectionRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { parentRunId: number; employeeIds: number[] }) =>
      payrollApi.createCorrectionRun(args.parentRunId, {
        employee_ids: args.employeeIds,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Periods holding two locked full runs (pre-correction-run era). */
export function usePeriodWarnings(enabled = true) {
  return useQuery({
    queryKey: payrollKeys.periodWarnings(),
    queryFn: ({ signal }) => payrollApi.getPeriodWarnings(signal),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** "Get payroll ready" checklist — GET /payroll/setup (HR admins). */
export function usePayrollSetup(enabled = true) {
  return useQuery({
    queryKey: payrollKeys.setup(),
    queryFn: ({ signal }) => payrollApi.getPayrollSetup(signal),
    enabled,
    retry: false,
    // Cheap, and it drives "can I run payroll?": always ask the server on
    // mount (it is also never persisted — lib/payroll-setup-queries).
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useEmployeePayrollSettings(employeeId: number, enabled = true) {
  return useQuery({
    queryKey: payrollKeys.employeeSettings(employeeId),
    queryFn: ({ signal }) =>
      payrollApi.getEmployeePayrollSettings(employeeId, signal),
    enabled: enabled && Number.isFinite(employeeId) && employeeId > 0,
  });
}

export function useUpdateEmployeePayrollSettings(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateEmployeePayrollSettingsRequest) =>
      payrollApi.updateEmployeePayrollSettings(employeeId, body),
    onSuccess: (data) => {
      qc.setQueryData(payrollKeys.employeeSettings(employeeId), data);
      // The work state moves professional tax in the salary breakup.
      invalidateSalaryBreakup(qc, employeeId);
      invalidatePayrollSetup(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Employee self-service (Phase 1a — My Pay)
// ---------------------------------------------------------------------------

/**
 * The signed-in user's own payslips from locked runs (newest first).
 * Any role — the backend derives the employee from the session, so no
 * id is passed and no role gate is needed here.
 */
export function useMyPayslips() {
  return useQuery({
    queryKey: payrollKeys.myPayslips(),
    queryFn: ({ signal }) => payrollApi.listMyPayslips(signal),
  });
}

/** My salary (Me → Pay): the caller's own salary breakup — the server
 *  binds the employee to the session, no id is sent. */
export function useMySalary() {
  return useQuery({
    queryKey: payrollKeys.mySalary(),
    queryFn: ({ signal }) => payrollApi.getMySalary(signal),
  });
}

/** People → Pay: the person's salary breakup (yearly CTC line). HR. */
export function useEmployeeSalary(employeeId: number, enabled = true) {
  return useQuery({
    queryKey: payrollKeys.employeeSalary(employeeId),
    queryFn: ({ signal }) => payrollApi.getEmployeeSalary(employeeId, signal),
    enabled: enabled && Number.isFinite(employeeId) && employeeId > 0,
  });
}

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

export function usePayrollRuns(year?: number) {
  return useQuery({
    queryKey: payrollKeys.runs(year),
    queryFn: ({ signal }) =>
      payrollApi.listRuns(year != null ? { year } : undefined, signal),
  });
}

export function usePayrollRun(id: number, enabled = true) {
  return useQuery({
    queryKey: payrollKeys.run(id),
    queryFn: ({ signal }) => payrollApi.getRun(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

/**
 * Invalidate every payroll bucket. A status change (new run, override
 * patch, lock) affects both the list and any open detail view, so the
 * cheaper coarse invalidation is the safe default — payroll runs are
 * infrequent relative to bell/notification reads.
 */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: payrollKeys.all });
  invalidatePayrollSetup(qc); // "has runs" moves the checklist
}

export function useCreatePayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePayrollRunRequest) =>
      payrollApi.createRun(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpsertPayrollOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      runId: number;
      employeeId: number;
      body: UpsertOverrideRequest;
    }) =>
      payrollApi.upsertOverride(vars.runId, vars.employeeId, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Refresh an unlocked draft (re-read employees, leave, attendance,
 *  loans; explicit overrides kept). */
export function useRecalculatePayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollApi.recalculateRun(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Discard an unlocked draft. Only the run LIST is invalidated here: the
 *  detail page that triggered the discard is still mounted, and
 *  refetching its (now deleted) run would flash a 404. The caller
 *  navigates away and then drops the run's cache entry. */
export function useDiscardPayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollApi.discardRun(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...payrollKeys.all, "runs"] });
      invalidatePayrollSetup(qc);
    },
  });
}

/** Overpaid salary arrears (F13): recover / dismiss / reopen. */
export function useRecoverSalaryArrear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { employeeId: number; parentRunId: number; runId: number }) =>
      payrollApi.recoverSalaryArrear({
        employee_id: args.employeeId,
        parent_run_id: args.parentRunId,
        run_id: args.runId,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDismissSalaryArrear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { employeeId: number; parentRunId: number; note: string }) =>
      payrollApi.dismissSalaryArrear({
        employee_id: args.employeeId,
        parent_run_id: args.parentRunId,
        note: args.note,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useReopenSalaryArrear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { employeeId: number; parentRunId: number }) =>
      payrollApi.reopenSalaryArrear({
        employee_id: args.employeeId,
        parent_run_id: args.parentRunId,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * Leave employees out of an OPEN regular draft ("skip this month", QA
 * payroll #21) — offered from the draft's Recalculate and the lock dialog
 * when they answer 422 inputs_missing (audit Q24). Returns the recalculated
 * run; every payroll bucket is invalidated so a later visit is not stale.
 */
export function useSkipPayrollRunEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { runId: number; employeeIds: number[]; reason?: string }) =>
      payrollApi.skipRunEmployees(args.runId, { employee_ids: args.employeeIds, reason: args.reason }),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * "Include again" a skipped employee on an open draft (audit Q25: the card
 * called the API directly and only updated the page's local copy, so the
 * cached run — and the next visit — still showed them left out, with no pay
 * row and a lock-confirm total short by their pay).
 */
export function useUnskipPayrollRunEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { runId: number; employeeId: number }) =>
      payrollApi.unskipRunEmployee(args.runId, args.employeeId),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Remove one employee from an open correction draft. */
export function useRemoveCorrectionEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { runId: number; employeeId: number }) =>
      payrollApi.removeRunEmployee(args.runId, args.employeeId),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useLockPayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      vars: number | { id: number; acknowledgeMonthNotEnded?: boolean },
    ) =>
      typeof vars === "number"
        ? payrollApi.lockRun(vars)
        : payrollApi.lockRun(vars.id, {
            acknowledgeMonthNotEnded: vars.acknowledgeMonthNotEnded,
          }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// Tenant settings (G5.4)
// ---------------------------------------------------------------------------

export function useTenantSettings(enabled = true) {
  return useQuery({
    queryKey: payrollKeys.tenantSettings(),
    queryFn: ({ signal }) => payrollApi.getTenantSettings(signal),
    enabled,
    // HR-admin only; a non-HR caller 403s. Don't retry — that's a
    // hard authz fail.
    retry: false,
    // Always refetch on mount so a hard page reload reflects the
    // latest persisted pt_state. Without this, the SyncStoragePersister
    // may restore a pre-save snapshot (throttle window) and the form
    // stays stuck on the stale value indefinitely (staleTime=5min).
    refetchOnMount: "always",
  });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTenantSettingsRequest) =>
      payrollApi.updateTenantSettings(body),
    onSuccess: (data: TenantSettings) => {
      // The PATCH echoes the full settings DTO (incl. pt_state, state_code,
      // and allow_public_posh_intake) — prime the cache so every consumer
      // (workspace form, POSH public-intake card) reflects the new value.
      qc.setQueryData(payrollKeys.tenantSettings(), data);
      // Also invalidate so an in-flight/background read re-fetches authoritative
      // state (the tenantSettings key sits outside payrollKeys.all).
      void qc.invalidateQueries({ queryKey: payrollKeys.tenantSettings() });
      // pt_state drives the payroll engine; invalidating runs is the
      // safer default in case HR is mid-flow.
      void qc.invalidateQueries({ queryKey: payrollKeys.all });
      invalidatePayrollSetup(qc); // PT state is a checklist step
    },
  });
}

// ---------------------------------------------------------------------------
// Compensation revision letters (G10 standalone flow)
// ---------------------------------------------------------------------------

/** HR: one employee's letters; omit employeeId for the caller's own. */
export function useCompRevisionLetters(employeeId?: number, enabled = true) {
  return useQuery({
    queryKey: payrollKeys.compLetters(employeeId),
    queryFn: ({ signal }) => payrollApi.listCompRevisionLetters(employeeId, signal),
    enabled,
  });
}

export function useCreateCompRevisionLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCompRevisionLetterRequest) => payrollApi.createCompRevisionLetter(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useIssueCompRevisionLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; applyToPayroll: boolean }) =>
      payrollApi.issueCompRevisionLetter(args.id, args.applyToPayroll),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useApplyCompRevisionLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollApi.applyCompRevisionLetter(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAcknowledgeCompRevisionLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollApi.acknowledgeCompRevisionLetter(id),
    onSuccess: () => invalidateAll(qc),
  });
}
